import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { HomeDto, MyPickDto, Side, SlateGameDto, WeekDetailDto } from '@pickem/shared';
import { lineForSide } from '@pickem/shared';
import { api, ApiError } from './client';
import { keys } from './queries';

/**
 * Pick auto-saving.
 *
 * The brief's hardest requirement is that a player must never be left unsure whether a
 * pick was recorded. That rules out fire-and-forget optimism: the tap has to feel instant,
 * but the interface must go back on itself the moment the server disagrees.
 *
 * So each game carries its own save state. A tap paints the selection immediately and
 * shows `saving`; success settles to `saved` and then fades to a plain selected state;
 * failure *rolls the selection back* and surfaces a retry. A pick that was rejected never
 * looks chosen.
 */

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface PickState {
  status: SaveStatus;
  error: string | null;
  /** True when the failure was the game locking, which needs a reload rather than a retry. */
  locked: boolean;
}

export interface PickController {
  stateFor: (slateGameId: string) => PickState;
  setPick: (slateGameId: string, selection: Side) => void;
  retry: (slateGameId: string) => void;
  /** How many writes are in flight, for a global "saving" affordance. */
  savingCount: number;
}

const SAVED_BADGE_MS = 2000;
const MAX_ATTEMPTS = 3;

const IDLE: PickState = { status: 'idle', error: null, locked: false };

/** Applies a change to one game in every cache that holds it. */
function patchGame(
  qc: ReturnType<typeof useQueryClient>,
  weekId: string,
  slateGameId: string,
  patch: (game: SlateGameDto) => SlateGameDto,
): void {
  const applyToWeek = (week: WeekDetailDto): WeekDetailDto => {
    const games = week.games.map((g) => (g.id === slateGameId ? patch(g) : g));
    const active = games.filter((g) => g.status !== 'voided');
    const myPicksMade = active.filter((g) => g.myPick !== null).length;
    return {
      ...week,
      games,
      myPicksMade,
      allComplete: active.length > 0 && myPicksMade === active.length,
    };
  };

  qc.setQueryData<WeekDetailDto>(keys.week(weekId), (prev) => (prev ? applyToWeek(prev) : prev));

  // The dashboard embeds the same week, so it has to move in lockstep or the pick counter
  // on Home would disagree with the one on the Picks screen.
  qc.setQueryData<HomeDto>(keys.home, (prev) =>
    prev?.week && prev.week.week.id === weekId ? { ...prev, week: applyToWeek(prev.week) } : prev,
  );
}

export function usePickController(weekId: string | null | undefined): PickController {
  const qc = useQueryClient();
  const [states, setStates] = useState<Record<string, PickState>>({});
  const [savingCount, setSavingCount] = useState(0);

  // Remembers the last side each game was asked to save, so `retry` knows what to resend.
  const pendingSelection = useRef<Record<string, Side>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const handles = timers.current;
    return () => {
      for (const handle of Object.values(handles)) clearTimeout(handle);
    };
  }, []);

  const setState = useCallback((slateGameId: string, next: PickState) => {
    setStates((prev) => ({ ...prev, [slateGameId]: next }));
  }, []);

  const save = useCallback(
    async (slateGameId: string, selection: Side, attempt = 1): Promise<void> => {
      if (!weekId) return;

      const week = qc.getQueryData<WeekDetailDto>(keys.week(weekId));
      const game = week?.games.find((g) => g.id === slateGameId);
      const previousPick = game?.myPick ?? null;

      pendingSelection.current[slateGameId] = selection;

      if (attempt === 1) {
        // Paint it immediately. This is the whole point of the interaction feeling instant.
        const optimistic: MyPickDto = {
          selection,
          pickedSpread: game ? lineForSide(game.contestSpreadHome, selection) : 0,
          result: 'pending',
          pointsAwarded: null,
          setByCommissioner: false,
          updatedAt: new Date().toISOString(),
        };
        patchGame(qc, weekId, slateGameId, (g) => ({
          ...g,
          myPick: optimistic,
          pickCount: previousPick ? g.pickCount : g.pickCount + 1,
        }));
        setSavingCount((n) => n + 1);
      }

      setState(slateGameId, { status: 'saving', error: null, locked: false });

      try {
        const saved = await api.put<MyPickDto>(`/slate-games/${slateGameId}/pick`, { selection });

        // Replace the optimistic value with the server's, so `pickedSpread` is the line the
        // server actually recorded rather than the one we guessed.
        patchGame(qc, weekId, slateGameId, (g) => ({ ...g, myPick: saved }));
        setState(slateGameId, { status: 'saved', error: null, locked: false });

        clearTimeout(timers.current[slateGameId]);
        timers.current[slateGameId] = setTimeout(() => {
          // The badge fades, but the selected state remains — that is the durable proof.
          setState(slateGameId, IDLE);
        }, SAVED_BADGE_MS);
      } catch (error) {
        const apiError = error instanceof ApiError ? error : null;

        if (apiError?.isRetryable && attempt < MAX_ATTEMPTS) {
          // Exponential backoff. A phone in a stadium drops packets constantly and this
          // recovers without the player noticing.
          const delay = 400 * 2 ** (attempt - 1);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return save(slateGameId, selection, attempt + 1);
        }

        // Out of retries: undo the optimistic paint so nothing shows as chosen that isn't.
        patchGame(qc, weekId, slateGameId, (g) => ({
          ...g,
          myPick: previousPick,
          pickCount: previousPick ? g.pickCount : Math.max(0, g.pickCount - 1),
        }));

        const locked = apiError?.code === 'PICK_LOCKED' || apiError?.code === 'SLATE_NOT_PUBLISHED';
        setState(slateGameId, {
          status: 'error',
          error: apiError?.message ?? 'Could not save that pick.',
          locked,
        });

        if (locked) {
          // The server's view of time has moved past ours; refetch so the UI stops
          // offering a game that can no longer be picked.
          void qc.invalidateQueries({ queryKey: keys.week(weekId) });
          void qc.invalidateQueries({ queryKey: keys.home });
        }
      } finally {
        if (attempt === 1) setSavingCount((n) => Math.max(0, n - 1));
      }
    },
    [qc, setState, weekId],
  );

  const setPick = useCallback(
    (slateGameId: string, selection: Side) => {
      void save(slateGameId, selection);
    },
    [save],
  );

  const retry = useCallback(
    (slateGameId: string) => {
      const selection = pendingSelection.current[slateGameId];
      if (selection) void save(slateGameId, selection);
    },
    [save],
  );

  const stateFor = useCallback(
    (slateGameId: string): PickState => states[slateGameId] ?? IDLE,
    [states],
  );

  return { stateFor, setPick, retry, savingCount };
}
